import React from "react";
import { FaTrash } from "../../utils/icons";
import { ComplexityBars } from "../ComplexityBars";
import type { UploadListItem } from "./types";

/**
 * A single row in the "Your uploads" list (LU-A10).
 *
 * Visually identical to the worklist `CaseRow` (same container + type ramp) so
 * the upload list reads as a worklist. Handles the three upload-specific states:
 *  - ready      → complexity bars + "N Findings" + lead/supporting findings
 *  - processing → "Analyzing" eyebrow with a spinner (study uploaded, AI pending)
 *  - failed     → "Upload failed" eyebrow (warn) + failure summary
 *
 * The worklist `CaseRow` is `StudyData`-coupled and not exported; for ready /
 * processing rows the logic lane MAY swap in the real `CaseRow` (uploads are
 * studies). `failed` has no worklist equivalent and stays here.
 */
interface UploadRowProps {
  item: UploadListItem;
  active?: boolean;
  onSelect?: (id: string) => void;
  /** Remove this upload from the list (revealed on hover). */
  onDelete?: (id: string) => void;
}

/** Friendly short id: last 8 chars of the first "::" segment (matches worklist). */
function shortId(id: string): string {
  return id.split("::")[0]?.slice(-8) || id.slice(-8);
}

function metaLine(item: UploadListItem): string {
  const parts: string[] = [];
  if (item.modality) parts.push(item.modality);
  if (item.studyDescription) parts.push(item.studyDescription);
  if (item.metaOverride) parts.push(item.metaOverride);
  else if (item.seriesCount != null) parts.push(`${item.seriesCount} series`);
  return parts.join(" · ");
}

export const UploadRow: React.FC<UploadRowProps> = ({
  item,
  active = false,
  onSelect,
  onDelete,
}) => {
  const meta = metaLine(item);
  return (
    <div
      data-testid="upload-row"
      onClick={() => onSelect?.(item.id)}
      className={`group px-4 py-3 border-b border-border hover:bg-surface-2 cursor-pointer transition-colors border-l-2 ${
        active ? "border-l-signal bg-signal-tint" : "border-l-transparent"
      }`}
    >
      {/* Top line: status eyebrow (left) · case id (right) */}
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 min-w-0">
          {item.status === "ready" && (
            <>
              <ComplexityBars level={item.complexity ?? 1} />
              <span className="text-[9px] font-bold uppercase tracking-widest text-signal-text-tint">
                {item.findingCount ?? 0} {item.findingCount === 1 ? "Finding" : "Findings"}
              </span>
            </>
          )}
          {item.status === "processing" && (
            <span className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest text-fg-muted">
              <span
                className="inline-block h-2 w-2 rounded-full border-[1.5px] border-border-strong border-t-signal animate-spin"
                aria-hidden="true"
              />
              Analyzing
            </span>
          )}
          {item.status === "failed" && (
            <span className="text-[9px] font-bold uppercase tracking-widest text-warn">
              Upload failed
            </span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          {/* Successful (ready) studies aren't deletable for now (Andrea, 2026-06-09).
              Code kept intentionally — re-enable by dropping the `!== "ready"` guard. */}
          {onDelete && item.status !== "ready" && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(item.id);
              }}
              data-testid="upload-row-delete"
              title="Remove from your uploads"
              className={`rounded p-1 transition-all hover:bg-surface-2 ${
                item.status === "failed"
                  ? "text-warn opacity-70 hover:opacity-100"
                  : "text-fg-faint opacity-0 group-hover:opacity-100 hover:text-fg-muted"
              }`}
            >
              <FaTrash className="h-2.5 w-2.5" />
            </button>
          )}
          <span className="text-[10px] text-fg-muted font-mono tabular-nums">
            {shortId(item.id)}
          </span>
        </span>
      </div>

      {/* Body */}
      {item.status === "ready" && (
        <>
          <div className="mt-1 truncate text-sm font-medium text-white">
            {item.leadFinding || "—"}
            {(item.findingCount ?? 0) > 1 && <span className="text-fg-muted">, …</span>}
          </div>
          {item.supportingText && (
            <div className="text-fg-muted text-xs line-clamp-2 mt-0.5">{item.supportingText}</div>
          )}
        </>
      )}
      {item.status === "processing" && (
        <div className="text-fg-muted text-xs mt-1">Uploaded · waiting on AI findings</div>
      )}
      {item.status === "failed" && item.failureSummary && (
        <div className="text-warn text-xs mt-1">{item.failureSummary}</div>
      )}

      {meta && <div className="text-[10px] text-fg-muted truncate mt-0.5">{meta}</div>}
    </div>
  );
};
